// ─── Hook ─────────────────────────────────────────────────────────────────────

import { useAlert } from '@ads/components-react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import {
  getApiV1ExamQuestionLibraryQueryKey,
  usePostApiV1ExamQuestionLibraryItem,
} from '@/shared/api/generated';

/**
 * Hook para criar uma nova questão na Biblioteca de Questões.
 *
 * Ao criar com sucesso, invalida o cache da listagem e navega de volta
 * para a página da biblioteca. Exibe alert de sucesso ou erro.
 *
 * @returns Função `mutate` para acionar a criação e estado `isPending`.
 *
 * @example
 * ```tsx
 * const { mutate: createQuestion, isPending } = useQuestionCreate();
 * createQuestion({ data: { componentType: 2, cultureInfo: 'pt-BR', label: '...', personAuthorId: 1, id: 0 } });
 * ```
 */
export function useQuestionCreate() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { t } = useTranslation('questionLibrary');
  const { alert } = useAlert();

  return usePostApiV1ExamQuestionLibraryItem({
    mutation: {
      onSuccess: () => {
        void queryClient.resetQueries({
          queryKey: getApiV1ExamQuestionLibraryQueryKey(),
        });
        alert({
          alertType: 'success',
          title: t('alerts.createSuccess.title'),
          description: t('alerts.createSuccess.description'),
        });
        void navigate({ to: '/question-library' });
      },
      onError: () => {
        alert({
          alertType: 'danger',
          title: t('alerts.createError.title'),
          description: t('alerts.createError.description'),
        });
      },
    },
  });
}
